import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Switch, Table, TableColumnType } from "antd";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Api, columnForAggregation, Facet } from "@vzbl/integrations";
import {
  attributeToTitle,
  makeTitleTerser,
  formatTitle,
} from "src/utils/strings";
import { ValueFormatter } from "src/utils/formatters";
import { asKey, fromKey } from "src/utils/fields";
import {
  FilterTag,
  getFilterTagEntitiesProp,
  propsForValue,
} from "src/search/components/inputs/FilterTag";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { Time } from "src/components/Time";
import { useEntitySelectors } from "src/search/useEntitySelectors";
import { SearchContext } from "src/search/useSearchContext";
import {
  SearchWindowConfig,
  SearchWindowInput,
  toSearchWindowConfig,
} from "src/search/components/inputs/SearchWindow";
import { SearchWindow } from "src/search/types";
import { ModalInsert } from "src/components/ModalInsert";
import { Flex } from "src/components/Flex";
import { TableColumns } from "./TableColumns";
import { VisualizationProps } from "./visualization-props";
import { pushOrReplaceTimer } from "./useQueryStringSync";
import { ContentView, ContentViews } from "./useContentView";
import { EmptyResultExplanation } from "./EmptyResultExplanation";
import {
  FormattingConfig,
  FormattingControls,
  hasTimeUnit,
  toFormattingConfig,
} from "./FormattingControls";
import { TableHeaderCustomComponent } from "pages/[orgId]/explore/lib/table/TableHeaderV2";
import { Field } from "src/search/types";
import { debounce } from "lodash";
import { TableLayoutSelector } from "./UnifiedTableVisualization";
import { AggregationConfig } from "./types";
import { TableColumnHeaderTooltip, VZBLTooltip } from "./tooltips/VZBLTooltips";
import { useTableSorting } from "./hooks/useTableSorting";
import styles from "./table/Table.module.css";

type Config = {
  columns: string[];
  allowWide: boolean;
  allowWrap: boolean;
  autoPageSize: boolean;
  layout?: "flat" | "pivot" | "nested";
} & FormattingConfig &
  SearchWindowConfig;

const toConfig: (data: Config | any) => Config = (data) => ({
  columns: Array.isArray(data?.columns) ? data.columns : [],
  allowWide: Boolean(data?.allowWide),
  allowWrap: Boolean(data?.allowWrap),
  autoPageSize:
    data?.autoPageSize === undefined ? true : Boolean(data?.autoPageSize),
  layout: data?.layout || "flat",
  ...toSearchWindowConfig(data),
  ...toFormattingConfig(data),
});

export function EntitiesTable(props: VisualizationProps) {
  // until this list is loaded, the UI doesn't know what to do.
  const { entitiesList, view } = props.context;
  const config = toConfig(view.config);

  const windowSetter = useRef({
    fn(window: SearchWindow) {
      props.context.entitiesList.setWindow(window);
    },
  });
  const { limit, sortBy, sortDirection } = config;

  // the sortByWindow has a special value 'aggregation' that
  // is intended to by in sync with the aggregation field.
  // the sync fails when e.g. clicking on a 'grouped' row.
  // the check below "sortByField == undefined" protects against that condition,
  // but a cleaner fix ought to be possible.
  const sortByField =
    sortBy === "aggregation"
      ? props.context.params.state.aggregations?.at(0)
      : sortBy;

  const searchWindow: SearchWindow = {
    limit,
    order:
      sortBy === "default" || sortByField == undefined
        ? undefined
        : [
            {
              by: sortByField,
              direction: sortDirection,
            },
          ],
  };
  const searchWindowRef = useRef(searchWindow);
  searchWindowRef.current = searchWindow;
  const windowCheckpoint = JSON.stringify({
    limit: searchWindow.limit,
    order: searchWindow.order,
  });
  const subUnsub = useRef({
    subscribe: entitiesList.subscribe,
    unsubscribe: entitiesList.unsubscribe,
  });
  subUnsub.current = {
    subscribe: entitiesList.subscribe,
    unsubscribe: entitiesList.unsubscribe,
  };
  useEffect(() => {
    const setWindow = windowSetter.current.fn;
    setWindow(searchWindowRef.current);
    const unsub = subUnsub.current.unsubscribe;
    subUnsub.current.subscribe();
    return () => unsub();
  }, [windowCheckpoint]);
  if (!entitiesList.isFetched)
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingOutlined />
      </div>
    );
  return <EntitiesListLoaded context={props.context} />;
}

function EntitiesListLoaded(props: {
  context: SearchContext & { view: ContentView; title: string };
}) {
  const { params, entitiesList, entity, view } = props.context;
  const config = toConfig(view.config);
  const data = useMemo(
    () => entitiesList.data?.data || [],
    [entitiesList.data]
  );
  const columnInfo = useMemo(
    () => entitiesList.data?.meta?.columnInfo || [],
    [entitiesList.data]
  );
  const { defaultDisplayColumns } = useEntitySelectors({ state: params.state });
  const meta_ = entitiesList.data?.meta;
  const meta = { page: 1, pageSize: 20, total: 0, ...meta_ };

  // Move useTableSorting to top level
  const { sortOrder } = useTableSorting({
    config: {
      sortBy: config.sortBy,
      sortDirection: config.sortDirection,
    },
  });

  const prevEntityTypeRef = useRef(params.state.select?.[0]);

  const defaultColumns = defaultDisplayColumns.map((f) => asKey(f));
  const serializedDefaultColumns = JSON.stringify(defaultColumns);

  // Effect to handle entity type changes
  useEffect(() => {
    const currentEntityType = params.state.select?.[0];
    // Reset columns if entity type changed or if columns don't match the current entity type
    if (
      currentEntityType !== prevEntityTypeRef.current ||
      !columnInfo.some((col) => config.columns.includes(col.key))
    ) {
      // Update the ref before making changes
      prevEntityTypeRef.current = currentEntityType;

      // Reset columns and force metadata refresh
      view.setConfig({
        ...config,
        columns: defaultColumns,
      });

      // Force a refresh of the entities list to get new metadata
      entitiesList.setWindow({
        limit: config.limit,
        columns: defaultColumns,
      });
    }
  }, [params.state.select, columnInfo, serializedDefaultColumns]);

  const items: ({ key: string } & Api.EntityItem)[] = useMemo(() => {
    const keys: Record<string, true> = {};
    return data.map(({ attributes }: Api.EntityData) => {
      keys[attributes.uuid] = true;
      return {
        key: JSON.stringify(attributes),
        entityUuid: attributes.entityUuid,
        ...attributes,
      };
    });
  }, [data]);

  const colCount = (config.columns || []).length;
  const fn = () => {
    if (colCount === 0) {
      pushOrReplaceTimer.ping();
      // Update the config immediately to reflect default columns
      view.setConfig({
        ...config,
        columns: defaultColumns,
      });
    }
  };

  const defaultsLoader = useRef({ fn });
  defaultsLoader.current = { fn };
  useEffect(() => {
    defaultsLoader.current.fn();
  }, [colCount]);

  // commenting this out, bc this cannot work right.
  // the intention is to configure the view so that columns that are specified in the query "userSpecifiedColumns"
  // are added to the view.
  // but this implementation makes it impossible to remove such a column (which is sometimes desirable)
  // and it can break when drilling down into a group with aggregation.
  // const specifiedCols = userSpecifiedColumns(params.state);
  // useEffect(() => {
  //   const existingCols = new Set(config.columns || []);
  //   const toAdd = specifiedCols
  //     .map((f) => asKey(f))
  //     .filter((c) => !existingCols.has(c));
  //   if (toAdd.length > 0) {
  //     pushOrReplaceTimer.ping();
  //     const newCols = [...config.columns, ...toAdd];
  //     view.setConfig({
  //       ...config,
  //       columns: newCols,
  //     });
  //   }
  // }, [specifiedCols, config, view]);

  const columns = useMemo(
    () =>
      config.columns.map((column, i) =>
        getColumnConfig(
          column,
          meta.columnInfo || [],
          config.allowWide ? false : i === 0,
          props.context,
          config.timeUnit,
          sortOrder
        )
      ),
    [
      meta.columnInfo,
      config.columns,
      config.allowWide,
      config.timeUnit,
      props.context,
      sortOrder,
    ]
  );

  const [rowMenu, setRowMenu] = useState<ReactNode>(null);
  const closeRowMenu = useCallback(() => {
    setRowMenu(null);
  }, []);
  const openRowMenu = useCallback(
    ({
      list,
      uuid,
      ...rowMenuProps
    }: {
      offset: { x: number; y: number };
      uuid: string;
      list: string[];
      target: HTMLElement;
      title: string;
      type: string;
    }) =>
      setRowMenu(
        <RowMenu
          {...rowMenuProps}
          onClose={closeRowMenu}
          onEntityOpen={() => entity.open({ uuid, list })}
        />
      ),
    [closeRowMenu, entity]
  );

  const atEnd =
    items.length < meta.pageSize || meta.page * meta.pageSize >= meta.total;
  return (
    <>
      <DynamicCanvas>
        {({ height }) => (
          <div
            className={`${styles.modernTable} clickable-rows ${
              config.allowWrap ? "allow-wrap" : ""
            }`}
            style={{
              height,
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              overflow: "visible",
            }}
          >
            <Table
              showHeader={items.length > 0}
              loading={{
                spinning: entitiesList.isLoading,
                indicator: <LoadingOutlined />,
              }}
              dataSource={items}
              scroll={items.length === 0 ? undefined : { x: "100%" }}
              components={{
                header: { cell: TableHeaderCustomComponent },
              }}
              onChange={(pagination, filters, sorter) => {
                if (Array.isArray(sorter)) {
                  return;
                }
                if (sorter.column) {
                  const sortBy = sorter.field as string;
                  const sortDirection =
                    sorter.order === "ascend" ? "ascending" : "descending";
                  view.setConfig({
                    ...config,
                    sortBy,
                    sortDirection,
                  });
                }
              }}
              pagination={{
                current: meta.page,
                total: meta.total,
                pageSize: config.autoPageSize
                  ? Math.floor((height - (29 + 33)) / 37)
                  : undefined, // th + paging / row height
                size: "small",
                style: { margin: "8px 0 0", height: 25, overflow: "visible" },
                showSizeChanger: !config.autoPageSize || config.allowWrap,
                onChange: (page, pageSize) =>
                  entitiesList.setPagination({ page, pageSize }),
              }}
              onRow={(record) => ({
                onClick(e) {
                  const target = e.target as HTMLTableCellElement;
                  if (!record.entityUuid) return;
                  const { left } = target.getBoundingClientRect();
                  const x = e.clientX - left;
                  const entityTypeKey = Object.keys(record).find((key) =>
                    key.endsWith("EntityType")
                  );
                  openRowMenu({
                    offset: { x: x - 24, y: 0 },
                    uuid: record.entityUuid,
                    list:
                      entitiesList.data?.data.map((d) => d.attributes.uuid) ||
                      [],
                    target,
                    title: (record as any).name || record.entityUuid,
                    type: `${record[entityTypeKey || ""] || "Event"}`,
                  });
                },
              })}
              columns={columns.map((col) => ({
                ...col,
                resizable: true,
                width: config.allowWide ? undefined : 200, // Default width when not wide
                minWidth: 100, // Minimum width for all columns
                maxWidth: config.allowWide ? undefined : 500, // Maximum width when not wide
              }))}
            />
            {items.length === 0 && meta.explanation !== undefined ? (
              <EmptyResultExplanation explanation={meta.explanation} />
            ) : (
              ""
            )}
          </div>
        )}
      </DynamicCanvas>
      {rowMenu}
    </>
  );
}

function getColumnConfig(
  column: string,
  columnInfo: Api.Schema.ColumnInfo,
  first: boolean,
  context: SearchContext & { view: ContentView; title: string },
  toUnit?: string,
  sortOrder?: "ascend" | "descend" | null
): TableColumnType<any> {
  const colInfo = columnInfo?.find((c) => c.key == column) || {
    type: "string",
    unit: undefined,
    ...fromKey(column),
  };
  const { type, attribute, unit } = colInfo;

  const config =
    type == "datetime" || type == "date"
      ? getDateColumnConfig(column)
      : attribute?.toLowerCase() == "integration"
      ? getIntegrationColumnConfig(column, context)
      : type == "number" && unit
      ? getNumberColumnConfig(column, unit, context, toUnit)
      : getDefaultColumnConfig(column, type, unit, context);

  const fixed = first ? "left" : undefined;
  const titleTerser = makeTitleTerser(context.params.state.select);
  const terseTitle = titleTerser(formatTitle(column));

  return {
    ...config,
    fixed,
    width: 200, // Default width
    onHeaderCell: () => ({
      style: { width: 200 },
      "data-col-index": column,
    }),
    onCell: () =>
      ({
        "data-col-index": column,
      } as React.TdHTMLAttributes<HTMLTableCellElement>),
    title: (
      <TableColumnHeaderTooltip field={fromKey(column)} title={terseTitle} />
    ),
    sorter: true,
    showSorterTooltip: false,
    sortOrder: context.view.config?.sortBy === column ? sortOrder : null,
  };
}

function getDateColumnConfig(property: string) {
  return {
    title: formatTitle(property),
    dataIndex: property,
    render: (value: string) => {
      return (
        value && (
          <div className="trunc">
            <Time relative={true} value={value} style={{ fontSize: "12px" }} />
          </div>
        )
      );
    },
  };
}

function getIntegrationColumnConfig(
  property: string,
  context: SearchContext & { view: ContentView; title: string }
) {
  const { target, attribute } = fromKey(property);
  return {
    title: formatTitle(property),
    dataIndex: property,
    render: (value: string, record: any) =>
      value && (
        <div className="trunc">
          <FilterTag
            context={context}
            properties={[{ target, attribute }]}
            values={[{ label: value, value }]}
            icon={<IntegrationIcon size="xsmall" source={value} />}
            entities={getFilterTagEntitiesProp("", record, context)}
          />
        </div>
      ),
  };
}

function getDefaultColumnConfig(
  property: string,
  type: string,
  unit: string | undefined,
  context: SearchContext & { view: ContentView; title: string }
) {
  const field = fromKey(property);

  return {
    title: formatTitle(property),
    dataIndex: property,
    render: (value: unknown, record: any) => {
      if (value && typeof value === "object" && value.hasOwnProperty("uuid")) {
        // Entity with possible avatar
        const entityValue = value as {
          uuid: string;
          value?: string;
          avatarUrl?: string;
        };
        const entityName = entityValue.value as string;
        const avatarUrl =
          entityValue.avatarUrl && entityValue.avatarUrl !== "__VZBL::null__"
            ? entityValue.avatarUrl
            : undefined;
        const items = getFilterTagEntitiesProp("", record, context);
        //if entityValue.uuid is not null, then render the entity tooltip
        if (entityValue.uuid !== "__VZBL::null__") {
          return (
            <VZBLTooltip
              record={record}
              value={entityValue.value}
              formattedValue={entityName}
              type="entity"
              field={{
                target: field.target,
                attribute: field.attribute,
                measureKey: "name",
              }}
              metadata={{
                entityName: entityName,
                entityAvatar: avatarUrl,
                email: record?.email,
                role: record?.role,
                lastActive: record?.lastActive,
                joinDate: record?.joinDate,
                status: record?.status,
              }}
              onClick={(e) => {
                // Handle click event if needed
                // This could be used to open entity details
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <FilterTag
                  context={context}
                  {...propsForValue(entityValue, record, field, context)}
                  entities={getFilterTagEntitiesProp("", record, context)}
                  options={{
                    showEntityOpener: true,
                    showFilters: true,
                    showGroupBy: true,
                    showAggregate: false,
                    showDrillDown: true,
                    showValueCopiers: false,
                  }}
                />
              </div>
            </VZBLTooltip>
          );
        }
      }
      // For regular values
      return (
        <FilterTag
          context={context}
          {...propsForValue(value, record, field, context)}
          entities={getFilterTagEntitiesProp("", record, context)}
          options={{
            showEntityOpener: true,
            showFilters: true,
            showGroupBy: true,
            showAggregate: false,
            showDrillDown: true,
            showValueCopiers: false,
          }}
        />
      );
    },
  };
}

function getNumberColumnConfig(
  property: string,
  unit: string,
  context: SearchContext & { view: ContentView; title: string },
  toUnit?: string
) {
  const field = fromKey(property);
  const formatValue = (v: unknown) => {
    try {
      const value = Number.parseFloat(v as string);
      return ValueFormatter.formatWithUnit(value, unit, toUnit);
    } catch {
      return v;
    }
  };

  const titleTerser = makeTitleTerser(context.params.state.select);
  const terseTitle = titleTerser(formatTitle(property));

  const aggregationConfig: AggregationConfig = {
    type: "value",
    field: field.attribute,
    key: property,
    title: terseTitle,
  };

  return {
    title: (
      <TableColumnHeaderTooltip
        field={field}
        title={terseTitle}
        type="aggregation"
        aggregation={aggregationConfig}
      />
    ),
    dataIndex: property,
    render: (value: unknown, record: any) => (
      <div style={{ display: "flex", alignItems: "center" }}>
        <FilterTag
          context={context}
          properties={[field]}
          values={[{ label: `${formatValue(value)}`, value: `${value}` }]}
          entities={getFilterTagEntitiesProp("", record, context)}
        />
      </div>
    ),
  };
}

export function EntitiesTableControls(props: {
  context: SearchContext & { view: ContentView; title: string };
}) {
  const { entitiesList, view, params } = props.context;
  const config = toConfig(view.config);
  const { defaultDisplayColumns } = useEntitySelectors({ state: params.state });

  // Handle layout changes
  const handleLayoutChange = (
    layout: "flat" | "pivot" | "nested",
    contentView: ContentViews,
    newConfig: any = {}
  ) => {
    view.set(contentView);
    view.setConfig({ ...view.config, ...newConfig, layout });
  };

  const columns = useMemo(
    () =>
      (entitiesList.data?.meta.columnInfo || [])
        .map((c) => c.key)
        .filter(
          (str) =>
            !([Facet.Actor, "integration", "uuid"] as string[]).includes(str) &&
            !str.endsWith(".uuid") // hacky
        )
        .sort((a, b) => {
          const aI = config.columns.indexOf(a);
          const bI = config.columns.indexOf(b);
          if (aI >= 0 && bI < 0) return -1;
          if (aI < 0 && bI >= 0) return 1;
          if (aI >= 0 && bI >= 0) return aI - bI;
          return a.toLowerCase().localeCompare(b.toLowerCase());
        }),
    [entitiesList.data, config.columns]
  );

  // Debounce column changes
  const updateColumns = useCallback(
    (newColumns: string[]) => {
      view.setConfig({ ...config, columns: newColumns });
    },
    [config, view]
  );

  const debouncedUpdateColumns = useMemo(
    () => debounce(updateColumns, 300),
    [updateColumns]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateColumns.cancel();
    };
  }, [debouncedUpdateColumns]);

  const aggCol = params.state.aggregations?.length
    ? columnForAggregation(params.state.aggregations!.at(0)!)
    : "";
  const sortColumns = columns.map((column) => ({
    label: column,
    value: column === aggCol ? "aggregation" : column,
  }));
  const columnFormulaNames =
    params.state.formulas?.map((formula) => formula.name) || [];

  const hasTime = hasTimeUnit(props.context.entitiesList);
  return (
    <div>
      {/* Add layout selector */}
      <TableLayoutSelector
        currentLayout={config.layout || "flat"}
        onLayoutChange={handleLayoutChange}
        context={props.context}
      />

      <div className="section" style={{ paddingLeft: 0 }}>
        <TableColumns
          selectedColumns={config.columns}
          defaultColumns={defaultDisplayColumns.map((f: Field) => asKey(f))}
          allColumns={columns}
          columnFormulaNames={columnFormulaNames}
          columnInfo={entitiesList.data?.meta.columnInfo || []}
          onSelectColumn={(column) => {
            const newColumns = [...config.columns];
            if (!newColumns.includes(column)) {
              newColumns.push(column);
              debouncedUpdateColumns(newColumns);
            }
          }}
          onRemoveColumn={(column) => {
            const newColumns = config.columns.filter((c) => c !== column);
            view.setConfig({ ...config, columns: newColumns });
          }}
          onReorderColumns={(newColumns) => {
            debouncedUpdateColumns(newColumns);
          }}
        />
      </div>
      <div className="section" style={{ paddingLeft: 0, marginTop: 16 }}>
        <div className="section-header">Display Options</div>
        <SearchWindowInput
          limit={config.limit}
          setLimit={(limit: number) => view.setConfig({ ...config, limit })}
          sortBy={config.sortBy}
          setSortBy={(sortBy: "ascending" | "descending") =>
            view.setConfig({ ...config, sortBy })
          }
          sortDirection={config.sortDirection}
          setSortDirection={(sortDirection: string) =>
            view.setConfig({ ...config, sortDirection })
          }
          columns={sortColumns}
        />
      </div>
      {hasTime && (
        <div style={{ width: "100%", marginBottom: 8 }}>
          <FormattingControls
            disabled={!hasTime}
            timeUnit={config.timeUnit}
            onChangeTimeUnit={(timeUnit) =>
              props.context.view.setConfig({ ...config, timeUnit })
            }
          />
        </div>
      )}
      <div style={{ width: "100%", marginBottom: 8 }}>
        <Flex spaceBetween style={{ width: "100%" }}>
          <span className="viz-label">Widen columns</span>
          <Switch
            checked={config.allowWide}
            onChange={(checked) =>
              view.setConfig({ ...config, allowWide: checked })
            }
            size="small"
          />
        </Flex>
      </div>
      <div style={{ width: "100%", marginBottom: 8 }}>
        <Flex spaceBetween style={{ width: "100%" }}>
          <span className="viz-label">Allow wrapping </span>
          <Switch
            checked={config.allowWrap}
            onChange={(checked) =>
              view.setConfig({ ...config, allowWrap: checked })
            }
            size="small"
          />
        </Flex>
      </div>
      <div style={{ width: "100%", marginBottom: 8 }}>
        <Flex spaceBetween style={{ width: "100%" }}>
          <span className="viz-label">Adaptive Paging</span>
          <Switch
            disabled={config.allowWrap}
            checked={!config.allowWrap && config.autoPageSize}
            onChange={(checked) =>
              view.setConfig({ ...config, autoPageSize: checked })
            }
            size="small"
          />
        </Flex>
      </div>
    </div>
  );
}

function RowMenu(props: {
  offset: { x: number; y: number };
  onClose(): void;
  onEntityOpen(): void;
  target: HTMLElement;
  title: string;
  type: string;
}) {
  return (
    <ModalInsert
      width={250}
      target={props.target}
      onClose={props.onClose}
      offset={props.offset}
    >
      <Button
        type="text"
        onClick={() => {
          props.onEntityOpen();
          props.onClose();
        }}
      >
        <Flex start style={{ width: 200, alignItems: "center" }} gap={6}>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          <span>Show this </span>
          <div style={{ fontWeight: 600, minWidth: 0 }}>
            <div className="trunc">{props.type}</div>
          </div>
        </Flex>
      </Button>
    </ModalInsert>
  );
}
